import { tick } from 'svelte'
import { db } from './db.js'
import {
  uniqueId, notifySessionList, relationalToLinear, omit, mergeOpenaiDiff,
} from './utils.js'
import { runLlmApi } from './llms.js'
import { CONFIG } from './config.svelte'
import { SessionScripts } from './SessionScripts.svelte.js'

export const USER = 'user'
export const ASSISTANT = 'assistant'
export const SYSTEM = 'system'

export class SessionState {
  sessionLoaded = $state(false)
  sessionData = $state()
  /**
   * @type {Array<{
   *   id: string,
   *   inputEl?: Element,
   *   createdAt?: number,
   *   parentId: string|null,
   *   role: string,
   *   content: string,
   *   thinking?: string,
   *   generation?: {api: string, model: string, promptTokens?: number, cachedPromptTokens?: number, completionTokens?: number, reasoningItems?: Array<object>, reasoningDetails?: Array<object>},
   *   toolCalls: Array<object>,
   *   markdown?: Boolean,
   *   collapsed?: Boolean,
   *   parameters?: object,
   *   customData: Array<{key: string, value: string, renderer?: string}>,
   *   depth: number,
   *   ddepth: number,
   *   linear: boolean,
   *   lastInChain: boolean,
   *   generating?: boolean,
   *   promptTokens?: number,
   *   completionTokens?: number,
   *   finishReason?: string,
   *   aborter?: AbortController,
   * }>}
   */
  messages = $state([])
  stopSaving = false  // post destroy / session delete
  isNewSession = false
  isSessionTitleChanged = false
  saveTimeoutId = null
  lastSaveTime = Date.now()

  constructor({ sessionId, autoReply }) {
    this.sessionId = sessionId
    this.autoReply = autoReply
    this.sessionScripts = new SessionScripts({
      sessionId: this.sessionId,
      getMessages: () => this.messages,
      getSessionData: () => this.sessionData,
      apiGenResponse: this.apiGenResponse,
      createMessage: this.createMessage,
      updateTree: this.updateTree,
    })

    $effect(() => {
      window._sessionInfo = this.sessionData
    })

    $effect(() => {
      this.sessionData?.title
      this.isSessionTitleChanged = true
    })

    $effect(() => {  // Autosave
      // Touch all fields for reactivity
      this.sessionData?.title
      this.sessionData?.windowLayout
      Object.keys(this.sessionData?.parameters || {})
      for (const i of this.messages) {  // TODO: is there a better way?
        i.parentId
        i.role
        i.content
        i.thinking
        JSON.stringify(i.generation)
        i.markdown
        i.collapsed
        i.toolCalls.forEach(toolCall => {
          toolCall.id
          toolCall.function?.name
          toolCall.function?.arguments
          toolCall.result
          toolCall.running
        })
        i.customData.forEach(d => { d.value; d.renderer })
      }
      window._sessionMessages = this.messages
      this.scheduleSave()
    })
  }

  updateTree = async () => {
    await tick()
    this.messages = relationalToLinear(this.messages)
    await tick()
  }

  saveSession = async (sessionId = this.sessionId) => {
    if (this.stopSaving) return
    if (!this.sessionLoaded) return
    if (this.isNewSession && this.messages.length === 1 && this.messages[0].content === '') return
    const obj = {
      ...$state.snapshot(this.sessionData),
      messages: this.messages.map(
        ({id, createdAt, parentId, markdown, role, content, thinking, generation, toolCalls, customData, parameters, collapsed}) => ({
          id,
          createdAt,
          parentId,
          markdown,
          role,
          content,
          thinking,
          generation: $state.snapshot(generation),
          toolCalls: $state.snapshot(toolCalls),
          customData: $state.snapshot(customData),
          parameters: $state.snapshot(parameters),
          collapsed,
        })
      ),
    }
    await (await db).put('sessionMeta', {
      id: sessionId,
      title: obj.title,
      createdAt: obj.createdAt,
    })
    await (await db).put('sessions', obj, sessionId)
    if (this.isNewSession || this.isSessionTitleChanged) {
      notifySessionList()
      this.isNewSession = false
      this.isSessionTitleChanged = false
    }
  }

  deleteSession = async () => {
    this.stopSaving = true
    const tx = (await db).transaction(['sessions', 'sessionMeta', 'scripts'], 'readwrite')
    await tx.objectStore('sessions').delete(this.sessionId)
    await tx.objectStore('sessionMeta').delete(this.sessionId)
    const allScripts = await tx.objectStore('scripts').getAll()
    for (const script of allScripts) {
      if (script.sessionId === this.sessionId) await tx.objectStore('scripts').delete(script.id)
    }
    await tx.done
    notifySessionList()
  }

  loadSession = async () => {
    let obj = await (await db).get('sessions', this.sessionId)
    if (obj === undefined) {
      this.isNewSession = true
      this.sessionData = {
        title: (new Date()).toLocaleString(),
        createdAt: new Date().valueOf(),
        parameters: {
          scriptsEnabled: (await (await db).getAll('scripts'))
            .filter(script => script.enabled)
            .map(script => script.id),
          toolsEnabled: true,
          temperature: 0,
          top_p: 1,
          frequency_penalty: 0,
          presence_penalty: 0,
          max_tokens: 0,
        },
      }
      this.createMessage()
      this.sessionLoaded = true
      await tick()

      if (this.autoReply) {
        this.messages[0].content = this.autoReply
        this.autoReply = undefined
        this.genResponse(this.messages[0])
      }
      return
    }
    const meta = await (await db).get('sessionMeta', this.sessionId)
    obj.title = meta.title
    obj.createdAt = meta.createdAt
    if (!obj.parameters) obj.parameters = {}
    if (obj.parameters.toolsEnabled === undefined) obj.parameters.toolsEnabled = true
    const {messages: loadedMessages, ...rest} = obj
    for (const m of loadedMessages) {
      if (!m.customData) m.customData = []
      if (!m.toolCalls) m.toolCalls = []
    }
    this.sessionData = rest
    this.messages = relationalToLinear(loadedMessages)
    this.sessionLoaded = true
  }

  _saveSession = async () => {
    await this.saveSession(this.sessionId)
    this.lastSaveTime = Date.now()
  }

  scheduleSave = (t = 500) => {
    if (this.saveTimeoutId !== null) clearTimeout(this.saveTimeoutId)
    if ((Date.now() - this.lastSaveTime) >= 2000) t = 0
    this.saveTimeoutId = setTimeout(this._saveSession, t)
  }

  setTitle = async (title) => {
    this.sessionData.title = title
    await (await db).put('sessionMeta', {
      id: this.sessionId,
      title: this.sessionData.title,
      createdAt: this.sessionData.createdAt,
    })
    notifySessionList()
  }

  createMessage = (values, index = 0) => {
    const newMessage = {
      id: uniqueId(),
      createdAt: new Date().valueOf(),
      parentId: null,
      role: USER,
      content: '',
      thinking: '',
      toolCalls: [],
      generating: false,
      markdown: false,
      customData: [],
      ...values,
    }
    const insertIndex = index === -1 ? this.messages.length : index
    this.messages.splice(insertIndex, 0, newMessage)
    const messageWithProxy = this.messages[insertIndex]
    this.messages = relationalToLinear(this.messages)
    return messageWithProxy
  }

  getChain = (message, regenerate = false) => {
    const chain = regenerate ? [] : [message]
    while (message = this.messages.find(p => p.id === message.parentId)) chain.unshift(message)
    return chain
  }

  apiGenResponse = (messageId, paramsReplace) => {
    const message = this.messages.find(m => m.id === messageId)
    if (message) return this.genResponse(message, false, paramsReplace)
  }

  runToolCalls = async (message, request, toolsByName = request._toolsByName) => {
    for (const toolCall of message.toolCalls || []) {
      if (request.signal.aborted || toolCall.result !== undefined) continue
      toolCall.running = true
      try {
        const tool = toolsByName[toolCall.function?.name]
        if (!tool) throw new Error(`Tool "${toolCall.function?.name}" not found`)
        const args = JSON.parse(toolCall.function?.arguments || '{}')
        const result = await tool(args, {request, toolMessage: message, toolCall})
        toolCall.result = typeof result === 'string' ? result : JSON.stringify(result) ?? ''
      } catch (e) {
        toolCall.result = `TOOL CALL FAILED: ${e.message}`
      } finally {
        toolCall.running = false
      }
    }
  }

  addToolMessages = (request) => {
    const messages = []
    for (const message of request.messages) {
      const storedMessage = this.messages.find(m => m.id === message._id)
      const toolCalls = storedMessage?.toolCalls || []
      if (!toolCalls.length) {
        messages.push(message)
        continue
      }
      messages.push({
        ...message,
        tool_calls: toolCalls.map(({result, running, ...toolCall}, index) => ({
          ...toolCall,
          id: toolCall.id || `tool_call_${index}`,
        })),
      })
      for (const [index, toolCall] of toolCalls.entries()) {
        if (toolCall.result === undefined) continue
        messages.push({
          role: 'tool',
          tool_call_id: toolCall.id || `tool_call_${index}`,
          content: [{type: 'text', text: toolCall.result}],
        })
      }
    }
    request.messages = messages
  }

  prepareTools = async (request, chain, params, defaultTools) => {
    request._toolsByName = {}
    if (params.toolsEnabled !== false) {
      const tools = request.tools.filter(tool =>
        tool?.definition?.function?.name && typeof tool.function === 'function'
      )
      for (const tool of tools) request._toolsByName[tool.definition.function.name] = tool.function
      if (tools.length) request.parameters.tools = tools.map(tool => tool.definition)
      else delete request.parameters.tools
      const historyTools = Object.fromEntries(defaultTools.map(tool => [tool.definition.function.name, tool.function]))
      Object.assign(historyTools, request._toolsByName)
      for (const message of chain) await this.runToolCalls(message, request, historyTools)
    }
    this.addToolMessages(request)
  }

  genResponse = async (message, regenerate = false, paramsReplace, scriptsData) => {
    const params = paramsReplace || $state.snapshot(this.sessionData.parameters)
    console.log('_genResponse', $state.snapshot(message), regenerate, params)

    const apiData = CONFIG.apis[params._api]
    if (!apiData.token) {
      alert(apiData.baseurl === 'codex://' ? 'Sign in with ChatGPT on the Settings page to use Codex.' : `Add token in settings for "${params._api}"`)
      throw Error('no token')
    }
    const modelInfo = CONFIG.models.find(i => (i.api === params._api && i.id === params.model))

    if (location.hostname === 'eimi.cns.wtf') {
      fetch('https://ut.cns.wtf/api/record/ei' + ' mi_gen'.trim())
    }

    let newMessage
    if (!regenerate) {
      newMessage = this.createMessage({
        parentId: message.id,
        role: ASSISTANT,
        generating: true,
        markdown: true,
        parameters: {...params},
      })
    } else {
      newMessage = message
      newMessage.content = ''
      newMessage.thinking = ''
      newMessage.toolCalls = []
      newMessage.generating = true
      newMessage.generation = undefined
      newMessage.promptTokens = undefined
      newMessage.completionTokens = undefined
      newMessage.parameters = {...params}
    }
    newMessage.aborter?.abort()
    const aborter = new AbortController()
    newMessage.aborter = aborter
    await tick()

    const request = {
      createdAt: new Date(),
      runner: runLlmApi,
      signal: aborter.signal,
      proxy: apiData.proxy === false ? false : true,
      baseurl: apiData.baseurl,
      api: params._api,
      token: apiData.baseurl === 'codex://' ? '' : apiData.token,
      model: params.model,
      completion: modelInfo.completion,
      sessionParameters: params,
      parameters: omit(params, ['_api', 'model', 'scriptsEnabled', 'toolsEnabled', 'max_tokens']),
      scriptsData: scriptsData || {},
      messages: this.getChain(message, regenerate).map(({id, role, thinking, content, generation}) => {
        const sameModel = generation?.api === params._api && generation?.model === params.model
        return {
          _id: id,
          role,
          _thinking: thinking,
          _generation: generation && {api: generation.api, model: generation.model},
          _reasoningItems: sameModel ? generation.reasoningItems : undefined,
          _reasoningDetails: sameModel ? generation.reasoningDetails : undefined,
          content: [{type: 'text', text: content}],
        }
      }),
    }
    if (request.baseurl.startsWith('anthropic') && params.max_tokens === 0) {
      request.parameters.max_tokens = modelInfo.max_tokens
    } else if (params.max_tokens !== 0) {
      request.parameters.max_tokens = params.max_tokens
    }

    console.log('runLlmApi before scripts:', {
      ...request,
      parameters: JSON.parse(JSON.stringify(request.parameters)),
      messages: JSON.parse(JSON.stringify(request.messages)),
    })

    const defaultTools = params.toolsEnabled !== false
      ? await this.sessionScripts.getTools(params.scriptsEnabled)
      : []
    request.tools = [...defaultTools]
    const scriptsOk = await this.sessionScripts.runOnRequest(
      request,
      newMessage,
      this.messages,
      params.scriptsEnabled
    )
    if (!scriptsOk) {
      newMessage.generating = false
      return
    }
    await this.prepareTools(request, this.getChain(message, regenerate), params, defaultTools)
    newMessage.generation = {api: request.api, model: request.model}

    try {
      newMessage.content = ''
      for await (const chunk of request.runner(request)) {
        if (typeof chunk === 'string') {
          newMessage.content += chunk
        } else {
          const responseModel = chunk?.rawChunk?.response?.model || chunk?.rawChunk?.model
          if (responseModel) newMessage.generation.model = responseModel
          if (chunk?.usage?.prompt_tokens !== undefined) {
            newMessage.generation.promptTokens = chunk.usage.prompt_tokens
            newMessage.promptTokens = chunk.usage.prompt_tokens
          }
          if (chunk?.usage?.cached_prompt_tokens !== undefined) newMessage.generation.cachedPromptTokens = chunk.usage.cached_prompt_tokens
          if (chunk?.usage?.completion_tokens !== undefined) {
            newMessage.generation.completionTokens = chunk.usage.completion_tokens
            newMessage.completionTokens = chunk.usage.completion_tokens
          }
          if (chunk?.thinking) newMessage.thinking += chunk.thinking
          if (chunk?.reasoningItem) {
            newMessage.generation.reasoningItems ||= []
            newMessage.generation.reasoningItems.push(chunk.reasoningItem)
          }
          if (chunk?.reasoningDetails) newMessage.generation.reasoningDetails = chunk.reasoningDetails
          if (chunk?.tool_call_delta) {
            const { index, ...diff } = chunk.tool_call_delta
            if (newMessage.toolCalls[index] === undefined) newMessage.toolCalls[index] = {}
            mergeOpenaiDiff(newMessage.toolCalls[index], diff)
          }
          if (chunk?.finishReason) newMessage.finishReason = chunk.finishReason
        }
        await tick()
      }
    } catch (e) {
      if (e.name === 'AbortError') return
      console.error(e)
      alert(`Request error:\n${e}`)
    } finally {
      newMessage.generating = false
      await this.sessionScripts.runPostRequest(request, newMessage)
    }
    if (!request.signal.aborted && params.toolsEnabled !== false && newMessage.toolCalls.length) {
      await this.runToolCalls(newMessage, request)
      if (request.signal.aborted) return
      if (!newMessage.content.trim()) newMessage.collapsed = true
      return this.genResponse(newMessage, false, params)
    }
    return {newMessage, request}
  }

  deleteMessageTree = async (messageId) => {
    const msg = this.messages.find(i => i.id === messageId)
    if (!msg) return
    msg.aborter?.abort()
    await tick()

    const idsToDelete = [msg.id]
    const addChildren = (current) => {
      const children = this.messages.filter(i => i.parentId === current.id)
      for (const child of children) {
        child.aborter?.abort()
        idsToDelete.push(child.id)
        addChildren(child)
      }
    }
    addChildren(msg)

    this.messages = relationalToLinear(this.messages.filter(i => !idsToDelete.includes(i.id)))
    this.scheduleSave(0)
  }

  moveMessage = async (messageId, direction) => {
    const currentIndex = this.messages.findIndex(m => m.id === messageId)
    if (currentIndex === -1) return
    const current = this.messages[currentIndex]

    if (direction === 'up') {
      if (currentIndex === 0) return
      const up = this.messages[currentIndex - 1]
      if (up.id === current.parentId) {
        // Case 1: up is shallower (is parent), becoming sibling and moving higher
        current.parentId = up.parentId
        this.messages[currentIndex - 1] = current
        this.messages[currentIndex] = up
      } else {
        // Case 2: up is same depth, set as parent
        // Case 3: up is deeper, drill up till same depth
        let i = currentIndex - 1
        while (i >= 0 && this.messages[i].depth > current.depth) i--
        current.parentId = this.messages[i].id
      }
    } else if (direction === 'down') {
      // Drilling past children
      let i = currentIndex + 1
      while (i < this.messages.length && this.messages[i].depth > current.depth) i++
      const down = this.messages[i]
      if (down?.depth === current.depth) {
        // Case 4: down is same depth, set as parent
        current.parentId = down.id
      } else {
        // Case 5: down is shallower OR end of messages, set parent to .parent.parent
        const parent = this.messages.find(m => m.id === current.parentId)
        current.parentId = parent ? parent.parentId : null
      }
    }

    this.messages = relationalToLinear(this.messages)
    return current.id
  }

  destroy = async () => {
    for (const i of this.messages) i.aborter?.abort()
    clearTimeout(this.saveTimeoutId)
    await this.sessionScripts.destroy()
    await this.saveSession(this.sessionId)
    this.stopSaving = true
  }
}
